import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { useDispatch } from "react-redux";
import AddButton, { DraftButton } from "../../../../components/buttons/button";
import CloseIcon from "../../../../assets/icons/close-icon";
import DeleteIcon from "../../../../assets/icons/delete-icon";
import InputField from "../../../../components/forms/input-field";
import successToast from "../../../../components/helper-components/success-toast";
import errorToast from "../../../../components/helper-components/error-toast";
import { warehouseBinGenerateAction } from "../../../../redux/actions/inventory-action";

const DIMENSIONS = ["building", "floor", "zone", "rack", "row", "column"];

// Mirrors MAX_BINS_PER_GENERATION_REQUEST in the backend warehouseValidation.ts
const MAX_BINS_PER_GENERATION = 5000;

// Same as backend rangeSize(): end - start + 1. Returns null for an
// incomplete/invalid range so the total can't be computed yet.
const rangeSize = (range) => {
  if (range?.start === "" || range?.end === "") return null;
  const start = Number(range?.start);
  const end = Number(range?.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return null;
  }
  return Math.floor(end) - Math.floor(start) + 1;
};

// Dimensions picked in the format dropdowns, in the order the user placed them
const getSelectedDimensions = (segments = []) => [
  ...new Set(segments.map((seg) => seg.dimension).filter(Boolean)),
];

const UNUSED_RANGE = { start: 0, end: 0, pad: 1 };

const getBinCalculation = (values) => {
  const dims = getSelectedDimensions(values.segments);
  const sizes = dims.map((dim) => rangeSize(values[dim]));
  const isValid = dims.length > 0 && sizes.every((size) => size !== null);
  const total = isValid ? sizes.reduce((acc, size) => acc * size, 1) : null;
  return { dims, sizes, total };
};

const DEFAULT_SEGMENTS = [{ text: "", dimension: "" }];

// Braces are reserved for the {dimension} placeholders in the template
const stripBraces = (value = "") => value.replace(/[{}]/g, "");

// [{ text: "B", dimension: "building" }, ...] + "X" -> "B{building}...X"
const buildTemplate = (segments = [], suffix = "") =>
  segments
    .map((seg) => `${seg.text ?? ""}${seg.dimension ? `{${seg.dimension}}` : ""}`)
    .join("") + (suffix ?? "");

// Bin number built from each dimension's start (first bin) or end (last bin)
// value, padded per its pad
const buildPreview = (values, bound = "start") =>
  values.segments
    .map((seg) => {
      const range = values[seg.dimension];
      const value =
        range && range[bound] !== ""
          ? String(Math.floor(Number(range[bound]))).padStart(
              Number(range.pad) || 1,
              "0"
            )
          : "";
      return `${seg.text ?? ""}${value}`;
    })
    .join("") + (values.suffix ?? "");

const segmentInputClass =
  "min-h-[42px] rounded-[8px] px-2 bg-[var(--input-bg)] text-[var(--input-color)] text-[length:var(--input-font-size)] border border-[var(--input-border)] focus:outline-none focus:border-[var(--input-focus)]";

const rangeSchema = Yup.object().shape({
  start: Yup.number().min(0, "Min 0").required("Required"),
  end: Yup.number()
    .min(Yup.ref("start"), "End >= start")
    .required("Required"),
  pad: Yup.number().min(1, "Min 1").max(10, "Max 10").required("Required"),
});

const validationSchema = Yup.object().shape({
  segments: Yup.array()
    .of(
      Yup.object().shape({
        text: Yup.string(),
        dimension: Yup.string().required("Select a dimension"),
      })
    )
    .min(1, "Add at least one dimension")
    .test("unique", "Each dimension can be used only once", (segments = []) => {
      const dims = segments.map((seg) => seg.dimension).filter(Boolean);
      return new Set(dims).size === dims.length;
    }),
  suffix: Yup.string(),
  // Only the dimensions used in the format need a range
  ...Object.fromEntries(
    DIMENSIONS.map((dim) => [
      dim,
      Yup.mixed().when("segments", ([segments], schema) =>
        getSelectedDimensions(segments).includes(dim) ? rangeSchema : schema
      ),
    ])
  ),
});

const GenerateBinsModal = ({
  isModalOpen = false,
  setIsModalOpen,
  warehouseId,
  onGenerated = () => {},
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    segments: DEFAULT_SEGMENTS,
    suffix: "",
    building: { start: 0, end: 0, pad: 1 },
    floor: { start: 0, end: 0, pad: 1 },
    zone: { start: 0, end: 0, pad: 1 },
    rack: { start: 0, end: 0, pad: 1 },
    row: { start: 0, end: 0, pad: 1 },
    column: { start: 0, end: 0, pad: 1 },
    max_quantity: 100,
    max_weight: "",
  };

  const onSubmit = (values) => {
    setLoading(true);
    const payload = {
      warehouse_id: warehouseId,
      bin_location_no: buildTemplate(values.segments, values.suffix),
      max_quantity: values.max_quantity === "" ? null : Number(values.max_quantity),
      max_weight: values.max_weight === "" ? null : Number(values.max_weight),
    };
    const selectedDims = getSelectedDimensions(values.segments);
    DIMENSIONS.forEach((dim) => {
      // Dimensions left out of the format collapse to a single value
      const range = selectedDims.includes(dim) ? values[dim] : UNUSED_RANGE;
      payload[dim] = {
        start: Number(range.start),
        end: Number(range.end),
        pad: Number(range.pad || 1),
      };
    });

    dispatch(
      warehouseBinGenerateAction(
        payload,
        (data) => {
          setLoading(false);
          successToast(
            `Generated ${data?.data?.length ?? "the"} bin(s) successfully`
          );
          setIsModalOpen(false);
          onGenerated();
        },
        (data) => {
          setLoading(false);
          errorToast(data?.message);
        }
      )
    );
  };

  return (
    <Transition show={isModalOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={() => setIsModalOpen(false)}
      >
        <div className="flex items-center justify-center min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="mx-auto w-full max-w-3xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
              <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ handleSubmit, values, errors, submitCount }) => {
                  const { dims, sizes, total } = getBinCalculation(values);
                  const exceedsLimit =
                    total !== null && total > MAX_BINS_PER_GENERATION;
                  return (
                    <Form>
                      <div className="flex justify-between px-6 py-5">
                        <h2 className="fw_500">Generate Bins</h2>
                        <button
                          type="button"
                          className="close-button bg-transparent border-none p-0 hover:bg-787878"
                          onClick={() => setIsModalOpen(false)}
                        >
                          <CloseIcon />
                        </button>
                      </div>

                      <div className="px-6 mb-4">
                        <p className="fs_14 fw_500 mb-2">
                          Bin Location No. Format
                        </p>
                        <FieldArray name="segments">
                          {({ push, remove }) => {
                            const usedDims = values.segments.map(
                              (seg) => seg.dimension
                            );
                            return (
                              <div className="flex flex-wrap items-center gap-2">
                                {values.segments.map((seg, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center gap-1"
                                  >
                                    <Field name={`segments[${index}].text`}>
                                      {({ field, form }) => (
                                        <input
                                          {...field}
                                          type="text"
                                          placeholder={
                                            index === 0 ? "Prefix" : "Text"
                                          }
                                          className={`${segmentInputClass} w-16`}
                                          onChange={(e) =>
                                            form.setFieldValue(
                                              field.name,
                                              stripBraces(e.target.value)
                                            )
                                          }
                                        />
                                      )}
                                    </Field>
                                    <Field
                                      as="select"
                                      name={`segments[${index}].dimension`}
                                      className={`${segmentInputClass} capitalize`}
                                    >
                                      <option value="">Select</option>
                                      {DIMENSIONS.map((dim) => (
                                        <option
                                          key={dim}
                                          value={dim}
                                          disabled={
                                            dim !== seg.dimension &&
                                            usedDims.includes(dim)
                                          }
                                        >
                                          {dim}
                                        </option>
                                      ))}
                                    </Field>
                                    {values.segments.length > 1 && (
                                      <button
                                        type="button"
                                        className="p-1 bg-transparent border-none"
                                        onClick={() => remove(index)}
                                      >
                                        <DeleteIcon />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <Field name="suffix">
                                  {({ field, form }) => (
                                    <input
                                      {...field}
                                      type="text"
                                      placeholder="Suffix"
                                      className={`${segmentInputClass} w-16`}
                                      onChange={(e) =>
                                        form.setFieldValue(
                                          field.name,
                                          stripBraces(e.target.value)
                                        )
                                      }
                                    />
                                  )}
                                </Field>
                                {values.segments.length < DIMENSIONS.length && (
                                  <button
                                    type="button"
                                    className="fs_12 fw_500 text-primary bg-transparent border border-D0D5DD rounded-md px-3 py-2"
                                    onClick={() =>
                                      push({
                                        text: "-",
                                        dimension:
                                          DIMENSIONS.find(
                                            (dim) => !usedDims.includes(dim)
                                          ) ?? "",
                                      })
                                    }
                                  >
                                    + Add
                                  </button>
                                )}
                              </div>
                            );
                          }}
                        </FieldArray>
                        {submitCount > 0 &&
                          (typeof errors.segments === "string" ||
                            Array.isArray(errors.segments)) && (
                            <p className="fs_12 text-F51D00 mt-1">
                              {typeof errors.segments === "string"
                                ? errors.segments
                                : "Select a dimension for every dropdown"}
                            </p>
                          )}
                        <p className="text-667085 fs_12 mt-2">
                          Generated template:{" "}
                          <span className="fw_600 text-344054">
                            {buildTemplate(values.segments, values.suffix) ||
                              "-"}
                          </span>
                        </p>
                      </div>

                      <div className="px-6 max-h-[45vh] overflow-y-auto">
                        <div className="grid grid-cols-[7rem_1fr_1fr_1fr] gap-3 items-center mb-2">
                          <span className="fs_12 fw_600 text-667085">
                            Dimension
                          </span>
                          <span className="fs_12 fw_600 text-667085">Start</span>
                          <span className="fs_12 fw_600 text-667085">End</span>
                          <span className="fs_12 fw_600 text-667085">Pad</span>
                        </div>
                        {dims.length === 0 && (
                          <p className="fs_12 text-667085 py-2">
                            Select a dimension in the format above to set its
                            range.
                          </p>
                        )}
                        {dims.map((dim) => (
                          <div
                            key={dim}
                            className="grid grid-cols-[7rem_1fr_1fr_1fr] gap-3 items-start"
                          >
                            <span className="fs_14 fw_500 capitalize pt-2">
                              {dim}
                            </span>
                            {["start", "end", "pad"].map((part) => (
                               <InputField
                                key={part}
                                type="number"
                                name={`${dim}.${part}`}
                                label=""
                                placeholder=""
                                isRequired={false}
                                containerClass="min-w-0"
                              />
                            ))}
                          </div>
                        ))}
                      </div>

                      <div
                        className={`mx-6 mt-3 px-4 py-3 rounded-md border ${
                          exceedsLimit
                            ? "border-F51D00"
                            : "border-D0D5DD"
                        }`}
                      >
                        <p className="fs_12 text-667085">
                          {dims.length === 0 && "No dimensions selected"}
                          {dims.map((dim, index) => (
                            <span key={dim}>
                              <span className="capitalize">{dim}</span> (
                              {sizes[index] ?? "-"})
                              {index < dims.length - 1 ? " × " : ""}
                            </span>
                          ))}
                        </p>
                        <p
                          className={`fs_14 fw_600 mt-1 ${
                            exceedsLimit ? "text-F51D00" : "text-344054"
                          }`}
                        >
                          Total bins to be generated:{" "}
                          {total !== null ? total.toLocaleString() : "-"}
                        </p>
                        {total !== null && (
                          <p className="fs_12 text-667085 mt-1">
                            First bin:{" "}
                            <span className="fw_600 text-344054">
                              {buildPreview(values, "start") || "-"}
                            </span>
                            <span className="mx-2">→</span>
                            Last bin:{" "}
                            <span className="fw_600 text-344054">
                              {buildPreview(values, "end") || "-"}
                            </span>
                          </p>
                        )}
                        {exceedsLimit && (
                          <p className="fs_12 text-F51D00 mt-1">
                            Exceeds the maximum of{" "}
                            {MAX_BINS_PER_GENERATION.toLocaleString()} bins per
                            request. Please reduce the ranges.
                          </p>
                        )}
                      </div>

                      <div className="flex gap-5 px-6 mt-3">
                        <div className="flex-1">
                          <InputField
                            type="number"
                            name="max_quantity"
                            placeholder="Optional"
                            label="Max Quantity"
                            isRequired={false}
                            labelWidth="8rem"
                          />
                        </div>
                        <div className="flex-1">
                          <InputField
                            type="number"
                            name="max_weight"
                            placeholder="Optional"
                            label="Max Weight"
                            isRequired={false}
                            labelWidth="8rem"
                          />
                        </div>
                      </div>

                      <div className="px-6 flex justify-between gap-4 py-5">
                        <DraftButton
                          onclkFn={() => setIsModalOpen(false)}
                          text="Cancel"
                        />
                        <AddButton
                          loading={loading}
                          isDisabled={exceedsLimit}
                          onclkFn={handleSubmit}
                          type="submit"
                          className={`bg-primary text-white px-8 py-2 rounded flex justify-center fw_500 text-sm ${
                            exceedsLimit ? "opacity-50" : ""
                          }`}
                          text="Generate"
                          prefixIcon={false}
                        />
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default GenerateBinsModal;
